import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import './Equity.css';
import InfoTip from './InfoTip';

const PERIOD_OPTIONS = [
  { value: 'mtd', label: 'MTD' },
  { value: 'ytd', label: 'YTD' },
  { value: '1m', label: '1 Month' },
  { value: '1y', label: '1 Year' },
  { value: 'all', label: 'All Time' },
];

function formatPct(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function pctClass(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return value >= 0 ? 'positive' : 'negative';
}

function formatPlain(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(2)}%`;
}

function formatDutchDate(isoDateStr) {
  if (!isoDateStr) return '—';
  const [y, m, d] = isoDateStr.split('-');
  if (!y || !m || !d) return isoDateStr;
  return `${d}-${m}-${y}`;
}

function formatCurrency(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `€${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Boundary date for a period, relative to the last date in the series. */
function periodStartDate(period, lastDate) {
  if (period === 'mtd') return new Date(Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth(), 1));
  if (period === 'ytd') return new Date(Date.UTC(lastDate.getUTCFullYear(), 0, 1));
  if (period === '1m') {
    const d = new Date(lastDate);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return d;
  }
  if (period === '1y') {
    const d = new Date(lastDate);
    d.setUTCFullYear(d.getUTCFullYear() - 1);
    return d;
  }
  return null; // 'all'
}

/** Slice `series` (full-history index, base 100 at inception) to the selected period,
 * rebasing so the period itself starts at index 100 - otherwise a YTD/1Y slice would
 * start at whatever the cumulative index happened to be, which reads as a random number
 * rather than "how did this period do".
 *
 * The rebase baseline is the last available point STRICTLY BEFORE the boundary (e.g.
 * the 31 July close for a "since 1 August" MTD boundary) - not the period's own first
 * day. That's what lets the period's first day get its own real daily return, instead
 * of being forced to be a zero-return anchor with nothing to compare against. The
 * returned array carries a `.hasExternalBase` flag so callers can tell a genuine prior
 * close was found (vs. the period boundary predating all available data, in which case
 * there really is no "day before" and the first point stays a true, un-computable start).
 */
function seriesForPeriod(series, period) {
  if (!series.length) return series;
  if (period === 'all') return series;

  const lastDate = new Date(series[series.length - 1].date);
  const boundary = periodStartDate(period, lastDate);

  let baseIdx = -1;
  for (let i = 0; i < series.length; i++) {
    if (new Date(series[i].date) < boundary) {
      baseIdx = i;
    } else {
      break;
    }
  }

  const hasExternalBase = baseIdx >= 0;
  const baseIndexValue = hasExternalBase ? series[baseIdx].index : series[0].index;
  const visibleStartIdx = hasExternalBase ? baseIdx + 1 : 0;

  const rebased = !baseIndexValue
    ? series.slice(visibleStartIdx)
    : series.slice(visibleStartIdx).map((point) => ({
        ...point,
        index: (point.index / baseIndexValue) * 100,
      }));

  rebased.hasExternalBase = hasExternalBase;
  return rebased;
}

/** Info button next to the page title. The explanation is rendered by InfoTip, which keeps it
 * inside the window (it used to be absolutely positioned and could run off the left edge). */
function InfoPopover({ children }) {
  return (
    <span style={{ display: 'inline-block', marginLeft: '0.5rem' }}>
      <InfoTip info={{ title: 'Time-Weighted Return', node: <div>{children}</div> }}>
        <button
          type="button"
          aria-label="Uitleg"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '28px', height: '28px', borderRadius: '50%',
            background: '#eef2ff', border: 'none', cursor: 'help', padding: 0, verticalAlign: 'middle',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#3b82f6" strokeWidth="2" />
            <path d="M12 11V16.5" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="7.5" r="1.25" fill="#3b82f6" />
          </svg>
        </button>
      </InfoTip>
    </span>
  );
}

const BROKER_OPTIONS = [
  { value: 'ibkr', label: 'IBKR' },
  { value: 'tastytrade', label: 'Tastytrade' },
  { value: 'all', label: 'All brokers' },
];

/** Time-Weighted Return - IBKR (default), Tastytrade, or both combined (in EUR). */
function TWR() {
  const [accounts, setAccounts] = useState([]);
  const [broker, setBroker] = useState('ibkr');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [period, setPeriod] = useState('ytd');
  const [rf, setRf] = useState('0');
  const [data, setData] = useState({ series: [], summary: {}, note: null, stats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/accounts/?page_size=all`);
      const accountsData = response.data.results || response.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const fetchTWR = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/portfolio/twr/`, {
        params: { broker, account: selectedAccount, rf: rf === '' ? 0 : rf },
      });
      setData({
        series: response.data.series || [],
        summary: response.data.summary || {},
        note: response.data.note || null,
        stats: response.data.stats || {},
      });
    } catch (err) {
      console.error('Error fetching TWR:', err);
      setError('Kon TWR niet laden');
    } finally {
      setLoading(false);
    }
  }, [broker, selectedAccount, rf]);

  useEffect(() => {
    fetchTWR();
  }, [fetchTWR]);

  const brokerAccounts = useMemo(
    () => accounts.filter((acc) => broker === 'all' || acc.broker === broker),
    [accounts, broker],
  );
  const allAccountsLabel = {
    ibkr: 'All IBKR accounts (combined)',
    tastytrade: 'All Tastytrade accounts (combined)',
    all: 'All accounts (combined, in EUR)',
  }[broker];

  const { summary, series, note, stats } = data;
  const periodStats = (stats && stats[period]) || null;
  const hasData = series.length > 0;
  const chartSeries = useMemo(() => seriesForPeriod(series, period), [series, period]);
  const periodSummaryKey = { ytd: 'ytd', '1y': 'one_year', all: 'all_time' }[period] || null;
  const periodLabel = PERIOD_OPTIONS.find((opt) => opt.value === period)?.label || '';
  // Backend precomputes all_time/ytd/one_year; for mtd/1m (and to stay consistent) derive
  // the selected period's return from the already-rebased chart series (last point vs its
  // own 100 baseline) rather than adding more fields to the API response.
  const selectedPeriodReturn = chartSeries.length
    ? chartSeries[chartSeries.length - 1].index - 100
    : null;

  // Daily table rows: equity, day-over-day result (from the index, so it's the flow-
  // adjusted TWR return, not a raw equity delta%), and the rolling/cumulative TWR since
  // the selected period's start (chartSeries is already rebased to 100 at that start).
  // The first row gets a real daily result too when the rebase used a genuine prior
  // close as its baseline (chartSeries.hasExternalBase) - the implicit "previous value"
  // for that day is the 100 baseline itself. Only a period that predates all available
  // data (no prior close exists at all) leaves the first row blank.
  // Newest first, matching this app's other tables (deposits, trades, ...).
  const dailyRows = useMemo(() => {
    const hasExternalBase = !!chartSeries.hasExternalBase;
    const rows = chartSeries.map((point, i) => {
      let dailyResult;
      if (i === 0) {
        dailyResult = hasExternalBase ? point.index - 100 : null;
      } else {
        dailyResult = (point.index / chartSeries[i - 1].index - 1) * 100;
      }
      return {
        date: point.date,
        equity: point.equity,
        dailyResult,
        cumulativeTwr: point.index - 100,
      };
    });
    return rows.slice().reverse();
  }, [chartSeries]);

  return (
    <div className="equity-container">
      <div className="equity-header">
        <h1 style={{ display: 'flex', alignItems: 'center' }}>
          Time-Weighted Return
          <InfoPopover>
            <p style={{ margin: '0 0 0.5rem 0' }}>
              <strong>Wat je hier ziet:</strong> de grafiek laat je portefeuille zien als
              een index die start op 100 - "115" betekent dus dat je beleggingen 15%
              gegroeid zijn, "90" betekent 10% verlies.
            </p>
            <p style={{ margin: '0 0 0.5rem 0' }}>
              Het belangrijkste punt: <strong>stortingen en opnames tellen niet mee als
              winst of verlies.</strong> Stort je €5.000, dan springt de index niet omhoog
              - want dat geld komt niet uit beleggingsresultaat, maar van je bankrekening.
              Alleen hoe je posities écht presteren beweegt de lijn.
            </p>
            <p style={{ margin: 0 }}>
              Daarom is dit een goede manier om je prestaties te meten: je totale equity
              stijgt bij elke storting en daalt bij elke opname, en zegt dus niets over hoe
              goed je handelt. TWR filtert dat eruit, zodat je je eigen resultaten eerlijk
              kunt vergelijken over de tijd, tussen accounts die heel verschillend gevuld
              zijn, of met een benchmark zoals de S&amp;P 500.
            </p>
          </InfoPopover>
        </h1>
        <div className="equity-controls">
          <div className="equity-control-group">
            <label htmlFor="twr-broker">Broker:</label>
            <select
              id="twr-broker"
              value={broker}
              onChange={(e) => { setBroker(e.target.value); setSelectedAccount('all'); }}
              className="equity-select"
            >
              {BROKER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="equity-control-group">
            <label htmlFor="twr-account">Account:</label>
            <select
              id="twr-account"
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              className="equity-select"
            >
              <option value="all">{allAccountsLabel}</option>
              {brokerAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.display_name || acc.broker_account_id}
                </option>
              ))}
            </select>
          </div>
          <div className="equity-control-group">
            <InfoTip k="risk_free"><label htmlFor="twr-rf">Risk-free %:</label></InfoTip>
            <input
              id="twr-rf"
              type="number"
              step="0.1"
              min="0"
              value={rf}
              onChange={(e) => setRf(e.target.value)}
              className="equity-select"
              style={{ width: '80px' }}
            />
          </div>
          <div className="equity-control-group">
            <label htmlFor="twr-period">Period:</label>
            <select
              id="twr-period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="equity-select"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Laden...</div>
      ) : error ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>{error}</div>
      ) : !hasData ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          Onvoldoende data om TWR te berekenen voor dit account.
        </div>
      ) : (
        <>
          <div className="equity-summary-cards">
            <InfoTip k="twr_selected" extra={`Gekozen periode: ${periodLabel}`}>
            <div
              className={`equity-card ${pctClass(selectedPeriodReturn)}`}
              style={{ outline: '2px solid #3b82f6' }}
            >
              <div className="equity-card-label">{periodLabel} TWR (selected)</div>
              <div className="equity-card-value"><span>{formatPct(selectedPeriodReturn)}</span></div>
            </div>
            </InfoTip>
            <InfoTip k="twr_all">
            <div
              className={`equity-card ${pctClass(summary.all_time)}`}
              style={periodSummaryKey === 'all_time' ? { outline: '2px solid #3b82f6' } : undefined}
            >
              <div className="equity-card-label">All-Time TWR</div>
              <div className="equity-card-value"><span>{formatPct(summary.all_time)}</span></div>
            </div>
            </InfoTip>
            <InfoTip k="twr_ytd">
            <div
              className={`equity-card ${pctClass(summary.ytd)}`}
              style={periodSummaryKey === 'ytd' ? { outline: '2px solid #3b82f6' } : undefined}
            >
              <div className="equity-card-label">YTD TWR</div>
              <div className="equity-card-value"><span>{formatPct(summary.ytd)}</span></div>
            </div>
            </InfoTip>
            <InfoTip k="twr_1y">
            <div
              className={`equity-card ${pctClass(summary.one_year)}`}
              style={periodSummaryKey === 'one_year' ? { outline: '2px solid #3b82f6' } : undefined}
            >
              <div className="equity-card-label">1-Year TWR</div>
              <div className="equity-card-value"><span>{formatPct(summary.one_year)}</span></div>
            </div>
            </InfoTip>
            <InfoTip k="twr_history">
            <div className="equity-card">
              <div className="equity-card-label">Full History</div>
              <div className="equity-card-value">
                <span style={{ fontSize: '0.9rem' }}>{summary.start_date} → {summary.end_date}</span>
              </div>
            </div>
            </InfoTip>
          </div>

          {periodStats && periodStats.days > 1 ? (
            <>
              <div className="equity-summary-cards" aria-label={`Risk statistics, ${periodLabel}`}>
                <InfoTip k="volatility" extra={`Periode: ${periodLabel}`}>
                <div className="equity-card">
                  <div className="equity-card-label">Volatility (ann.)</div>
                  <div className="equity-card-value"><span>{formatPlain(periodStats.volatility_pct)}</span></div>
                </div>
                </InfoTip>
                <InfoTip k="sharpe" extra={`Risicovrije rente in deze berekening: ${rf || 0}%`}>
                <div className="equity-card">
                  <div className="equity-card-label">Sharpe</div>
                  <div className="equity-card-value"><span>{periodStats.sharpe === null || periodStats.sharpe === undefined ? '—' : periodStats.sharpe.toFixed(2)}</span></div>
                </div>
                </InfoTip>
                <InfoTip k="max_drawdown" extra={`Van top ${formatDutchDate(periodStats.max_drawdown_peak_date)} tot dal ${formatDutchDate(periodStats.max_drawdown_trough_date)}`}>
                <div className="equity-card negative">
                  <div className="equity-card-label">Max drawdown</div>
                  <div className="equity-card-value"><span>{periodStats.max_drawdown_pct ? `-${periodStats.max_drawdown_pct.toFixed(2)}%` : '0.00%'}</span></div>
                </div>
                </InfoTip>
                <InfoTip k="current_drawdown" extra={`Hoogste stand in deze periode: ${formatDutchDate(periodStats.current_drawdown_peak_date)} · laatste stand: ${formatDutchDate(periodStats.end_date)}`}>
                <div className="equity-card">
                  <div className="equity-card-label">Current drawdown</div>
                  <div className="equity-card-value"><span>{periodStats.current_drawdown_pct ? `-${periodStats.current_drawdown_pct.toFixed(2)}%` : '0.00%'}</span></div>
                </div>
                </InfoTip>
                <InfoTip k="calmar" extra={periodStats.annualised_return_pct === null || periodStats.annualised_return_pct === undefined ? 'Nog te weinig data om te annualiseren (90+ dagen nodig)' : `Geannualiseerd rendement: ${periodStats.annualised_return_pct.toFixed(1)}%`}>
                <div className="equity-card">
                  <div className="equity-card-label">Calmar</div>
                  <div className="equity-card-value"><span>{periodStats.calmar === null || periodStats.calmar === undefined ? '—' : periodStats.calmar.toFixed(2)}</span></div>
                </div>
                </InfoTip>
                <InfoTip k="best_worst_day">
                <div className="equity-card">
                  <div className="equity-card-label">Best / worst day</div>
                  <div className="equity-card-value">
                    <span style={{ fontSize: '0.95rem' }}>{formatPct(periodStats.best_day_pct)} / {formatPct(periodStats.worst_day_pct)}</span>
                  </div>
                </div>
                </InfoTip>
              </div>
              <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Risk statistics for {periodLabel}, from daily time-weighted returns (deposits and withdrawals excluded).
                Calmar and annualised figures need 90+ days; Sharpe needs 10+.
              </p>
            </>
          ) : null}

          {note ? (
            <div style={{
              padding: '0.75rem 1rem', marginBottom: '1rem', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', fontSize: '0.875rem',
            }}>
              ⚠ {note}
            </div>
          ) : null}

          <div style={{
            background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)', padding: '1rem', height: '420px',
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartSeries} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  minTickGap={40}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  domain={['auto', 'auto']}
                  tickFormatter={(v) => v.toFixed(0)}
                />
                <Tooltip
                  formatter={(value) => [`${Number(value).toFixed(2)}`, 'TWR index (base 100)']}
                  labelStyle={{ color: '#1a1a1a' }}
                />
                <ReferenceLine y={100} stroke="#9ca3af" strokeDasharray="4 4" />
                <Line
                  type="monotone"
                  dataKey="index"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="equity-table-container" style={{ marginTop: '1rem' }}>
            <table className="equity-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <InfoTip k="th_equity"><th>Equity</th></InfoTip>
                  <InfoTip k="th_daily_result"><th>Daily Result</th></InfoTip>
                  <InfoTip k="th_cumulative_twr"><th>Cumulative TWR ({periodLabel})</th></InfoTip>
                </tr>
              </thead>
              <tbody>
                {dailyRows.map((row) => (
                  <tr key={row.date}>
                    <td>{formatDutchDate(row.date)}</td>
                    <td>{formatCurrency(row.equity)}</td>
                    <td className={pctClass(row.dailyResult)}>{formatPct(row.dailyResult)}</td>
                    <td className={pctClass(row.cumulativeTwr)} style={{ fontWeight: 600 }}>
                      {formatPct(row.cumulativeTwr)}
                    </td>
                  </tr>
                ))}
                {dailyRows.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                      No data for this period.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default TWR;
